import type { XrefPump, XrefPumps } from "@/composables/forms/formTypes";
import type { LabelledDataSet } from "@/components/types";
import type { NumericPoint } from "@incytestudios/ddv";
import { normalizeTornadoPoints } from "@/libs/charting";
import type {
  PumpChartDataPointCalculator,
  PumpChartDataPointParams,
  PumpTornadoChartContext
} from "../../d";
import { getPowerPerStageBHP } from "@/libs/calculations/pumps/pumpData";

export const getRequiredBhpDataPoint: PumpChartDataPointCalculator = (
  params: PumpChartDataPointParams
): NumericPoint => {
  const {
    flowRateBblD,
    frequencyRatio,
    pumpWearBHPCorrectionRatio,
    ascendingOrderPumps,
    fluidDensitySpGr,
    bhpCalibrationMultiplier = 1
  } = params;

  const data = getSimplifiedRequiredBhp(
    flowRateBblD,
    frequencyRatio,
    ascendingOrderPumps,
    fluidDensitySpGr,
    pumpWearBHPCorrectionRatio
  );

  return {
    x: flowRateBblD,
    y: data.bhp * bhpCalibrationMultiplier
  };
};

// This helps align data from getSimplifiedRequiredBhp with actual required bhp values
// by calculating a multiplier based on the real pump frequency and flow rate.
// This is necessary because the simplified bhp calculation doesn't capture all the nuances
// of the real pump performance, but we want the curve to align with the known required bhp
// at the actual operating point. The difference being in the real calcs fluidDensity changes
// from stage to stage whereas in the simplified version we use an average fluidDensity and
// just loop through each pump
export const getBhpCalibrationMultiplier = ({
  frequencyRatio,
  realFrequencyRatio,
  pumpDischargeFlowRateBBLD,
  pumpRequiredBrakeHP,
  ascendingOrderPumps,
  fluidDensitySpGr,
  pumpWearBHPCorrectionRatio
}: {
  frequencyRatio: number;
  realFrequencyRatio: number;
  pumpDischargeFlowRateBBLD: number;
  pumpRequiredBrakeHP: number;
  ascendingOrderPumps: XrefPumps;
  fluidDensitySpGr: number;
  pumpWearBHPCorrectionRatio: number;
}) => {
  const realRatio = realFrequencyRatio || 1;
  const frequencyScale = realRatio === 0 ? 1 : frequencyRatio / realRatio;
  const scaledReferenceFlowRate = pumpDischargeFlowRateBBLD * frequencyScale;
  const scaledReferenceRequiredPumpBhp =
    pumpRequiredBrakeHP * frequencyScale ** 3;

  const operatingSimplifiedBhp = getSimplifiedRequiredBhp(
    scaledReferenceFlowRate,
    frequencyRatio,
    ascendingOrderPumps,
    fluidDensitySpGr,
    pumpWearBHPCorrectionRatio
  ).bhp;

  if (!isFinite(operatingSimplifiedBhp) || operatingSimplifiedBhp <= 0) {
    return 1;
  }

  return scaledReferenceRequiredPumpBhp / operatingSimplifiedBhp;
};

/**
 * This is a simplified version of the processPumpData() calculations in pumps.ts
 * used for generating the curve on the Tornado BHP chart. This runs on a per pump
 * basis instead of a per stage basis. If something changes in processPumpData we
 * need to make changes here as well and vice versa.
 */
export const getSimplifiedRequiredBhp = (
  flowRateBblD: number,
  frequencyRatio: number,
  ascendingOrderPumps: XrefPumps,
  fluidDensitySpecificGravity: number,
  pumpWearBHPCorrectionRatio: number
) => {
  const bhpData = ascendingOrderPumps.reduce(
    (a, xrefPump) => {
      const pump = xrefPump.r_pump;
      if (!pump) return a;
      // Correct flow rate to freq like we do inside processPumpData
      // Then limit flow rate to calc bhp if it exceeds graphMax like
      // processPumpData does as well
      const totalFlowRateCorrectedSTBD =
        (1 / xrefPump.rate_correction_ratio) * (flowRateBblD / frequencyRatio);

      const pumpIntakeFlowRateExceedsGraphMax =
        totalFlowRateCorrectedSTBD >
        (pump.max_graph_rate_standard_pump_bpd || 0);

      // If flow rate exceeds graphMax, use graphMax for bhp
      const flowRateForBHP = pumpIntakeFlowRateExceedsGraphMax
        ? pump.max_graph_rate_standard_pump_bpd || 0
        : totalFlowRateCorrectedSTBD;

      const rateCorrectedPumps =
        flowRateForBHP === totalFlowRateCorrectedSTBD
          ? a.rateCorrectedPumps
          : [...a.rateCorrectedPumps, xrefPump];
      const bhpPerStage = getPowerPerStageBHP(
        flowRateForBHP,
        xrefPump,
        frequencyRatio,
        fluidDensitySpecificGravity,
        pumpWearBHPCorrectionRatio
      );

      const bhpPerPump = bhpPerStage * (xrefPump.total_stages || 1);
      const bhp = a.bhp + bhpPerPump;
      return {
        bhp,
        rateCorrectedPumps
      };
    },
    {
      bhp: 0,
      rateCorrectedPumps: [] as XrefPump[]
    }
  );
  return {
    bhp: bhpData.bhp,
    rateCorrectedPumps: bhpData.rateCorrectedPumps
  };
};

export const getRequiredDataSetsBhp = (
  context: PumpTornadoChartContext
): {
  dataSet: LabelledDataSet;
  adjustedPumpHzFrequency: number;
  originalPumpHzFrequency: number;
  producedBhpRateCorrectedPumps: XrefPump[];
  estimatedReqBhpPointTotal?: NumericPoint;
  estimatedReqBhpPointPump?: NumericPoint;
}[] => {
  const {
    baseXUnit,
    targetXUnit,
    targetXUnitLabel,
    baseBhpYUnit,
    targetBhpYUnit,
    targetBhpYUnitLabel,
    pumpDischargeFlowRateBBLD,
    pumpIntakeFlowRateBBLD,
    totalRequiredBrakeHP,
    pumpRequiredBrakeHP,
    pumpHz,
    hypotheticalPumpFrequenciesIncludeRealHz,
    pumpWearBHPCorrectionRatio,
    fluidDensitySpGr,
    ascendingOrderPumps
  } = context;

  const realFrequencyRatio = hypotheticalPumpFrequenciesIncludeRealHz.find(
    hf => hf.frequency === (context.pumpHz || 0)
  )?.frequencyRatio;

  // This function needs to be defined here so it can access the realFrequencyRatio for accurate
  // calibration of the BHP curve to the real operating point. We want the curve to align with the known
  // required bhp at the actual operating point, even if that means the shape of the curve is a bit off
  // at other frequencies due to the simplifications in the bhp calculation.
  const resolveBhpCalibrationMultiplier = (frequencyRatio: number) =>
    getBhpCalibrationMultiplier({
      frequencyRatio,
      realFrequencyRatio: realFrequencyRatio || 1,
      pumpDischargeFlowRateBBLD,
      pumpRequiredBrakeHP,
      ascendingOrderPumps,
      fluidDensitySpGr,
      pumpWearBHPCorrectionRatio
    });

  // We used to use hypotheticalPumpFrequencies here, but we need to include the real
  // pump Hz line as well to find the estimated points, then we filter it out
  const dataSets = hypotheticalPumpFrequenciesIncludeRealHz.map<{
    dataSet: LabelledDataSet;
    adjustedPumpHzFrequency: number;
    originalPumpHzFrequency: number;
    producedBhpRateCorrectedPumps: XrefPump[];
    estimatedReqBhpPointTotal?: NumericPoint;
    estimatedReqBhpPointPump?: NumericPoint;
  }>(hf => {
    const { frequencyRatio, frequency } = hf;
    const bhpCalibrationMultiplier =
      resolveBhpCalibrationMultiplier(frequencyRatio);
    const graphMaxBblD =
      (context.pump.r_pump?.max_graph_rate_standard_pump_bpd || 0) *
      frequencyRatio *
      context.pump.rate_correction_ratio;

    const producedBhpData = context.hypotheticalFlowRates
      .filter(rate => rate <= graphMaxBblD)
      .map(flowRateStbD => {
        const bhpDataPoint = getSimplifiedRequiredBhp(
          flowRateStbD,
          frequencyRatio,
          ascendingOrderPumps,
          fluidDensitySpGr,
          pumpWearBHPCorrectionRatio
        );
        return {
          flowRateStbD,
          bhpDataPoint
        };
      });
    const producedBhpPoints = producedBhpData
      .map<NumericPoint>(bhpDataPointResult => {
        return {
          x: bhpDataPointResult.flowRateStbD,
          y: bhpDataPointResult.bhpDataPoint.bhp * bhpCalibrationMultiplier
        };
      })
      .map<NumericPoint>(pt => ({
        ...pt,
        y: pt.y
      }));
    const adjustedPumpHzFrequency = frequency * (context.motorPoles / 2);

    const producedBhpRateCorrectedPumps = producedBhpData.reduce(
      (a, producedBhpData) => {
        const existingPumpIds = a.map(p => p.id);
        const pumpsToAdd =
          producedBhpData.bhpDataPoint.rateCorrectedPumps.filter(
            p => existingPumpIds.indexOf(p.id) === -1
          );
        return [...a, ...pumpsToAdd];
      },
      [] as XrefPump[]
    );

    const normalizedTornadoBhpPoints = normalizeTornadoPoints(
      producedBhpPoints,
      baseXUnit,
      targetXUnit,
      baseBhpYUnit,
      targetBhpYUnit
    );

    return {
      dataSet: {
        label: `${adjustedPumpHzFrequency}hz`,
        color: "var(--gray-600);",
        xAxisLabel: `(${targetXUnitLabel})`,
        yAxisLabel: `(${targetBhpYUnitLabel})`,
        curveType: "catmull-rom",
        points: normalizedTornadoBhpPoints
      },
      adjustedPumpHzFrequency,
      originalPumpHzFrequency: frequency,
      producedBhpRateCorrectedPumps
    };
  });

  const realHzBhpCalibrationMultiplier = getBhpCalibrationMultiplier({
    frequencyRatio: realFrequencyRatio || 1,
    realFrequencyRatio: realFrequencyRatio || 1,
    pumpDischargeFlowRateBBLD,
    pumpRequiredBrakeHP,
    ascendingOrderPumps,
    fluidDensitySpGr,
    pumpWearBHPCorrectionRatio
  });
  const realDataSet = dataSets.find(
    ds => ds.originalPumpHzFrequency === pumpHz
  );

  const totalApproxFlowRate = solveFlowRateForReqBHP(
    pumpDischargeFlowRateBBLD,
    pumpIntakeFlowRateBBLD,
    totalRequiredBrakeHP / realHzBhpCalibrationMultiplier,
    realFrequencyRatio || 1,
    ascendingOrderPumps,
    fluidDensitySpGr,
    pumpWearBHPCorrectionRatio
  ).flowRate;
  const pumpApproxFlowRate = solveFlowRateForReqBHP(
    pumpDischargeFlowRateBBLD,
    pumpIntakeFlowRateBBLD,
    pumpRequiredBrakeHP / realHzBhpCalibrationMultiplier,
    realFrequencyRatio || 1,
    ascendingOrderPumps,
    fluidDensitySpGr,
    pumpWearBHPCorrectionRatio
  ).flowRate;

  const estimatedReqBhpPointTotal = {
    x: totalApproxFlowRate,
    y:
      getSimplifiedRequiredBhp(
        totalApproxFlowRate,
        realFrequencyRatio || 1,
        ascendingOrderPumps,
        fluidDensitySpGr,
        pumpWearBHPCorrectionRatio
      ).bhp * realHzBhpCalibrationMultiplier
  };
  const estimatedReqBhpPointPump = {
    x: pumpApproxFlowRate,
    y:
      getSimplifiedRequiredBhp(
        pumpApproxFlowRate,
        realFrequencyRatio || 1,
        ascendingOrderPumps,
        fluidDensitySpGr,
        pumpWearBHPCorrectionRatio
      ).bhp * realHzBhpCalibrationMultiplier
  };
  if (realDataSet) {
    realDataSet.estimatedReqBhpPointTotal = estimatedReqBhpPointTotal;
    realDataSet.estimatedReqBhpPointPump = estimatedReqBhpPointPump;
  }
  return dataSets;
};

export const solveFlowRateForReqBHP = (
  pumpDischargeFlowRateBBLD: number,
  pumpIntakeFlowRateBBLD: number,
  requiredBHP: number,
  frequencyRatio: number,
  ascendingOrderPumps: XrefPumps,
  fluidDensitySpecificGravity: number,
  pumpWearBHPCorrectionRatio: number
) => {
  let MIN_STARTING_FLOW_RATE = pumpDischargeFlowRateBBLD;
  let MAX_FLOW_RATE = pumpIntakeFlowRateBBLD;
  let minFlowRate = pumpDischargeFlowRateBBLD;
  let maxFlowRate = pumpIntakeFlowRateBBLD;

  const isDischargeFlowRateLessThanIntake =
    pumpDischargeFlowRateBBLD < pumpIntakeFlowRateBBLD;
  // If for some reason the intake rate is less than the discharge rate, swap them
  if (!isDischargeFlowRateLessThanIntake) {
    MIN_STARTING_FLOW_RATE = pumpIntakeFlowRateBBLD;
    MAX_FLOW_RATE = pumpDischargeFlowRateBBLD;
    minFlowRate = pumpIntakeFlowRateBBLD;
    maxFlowRate = pumpDischargeFlowRateBBLD;
  }
  let searchStartFlowRate = MIN_STARTING_FLOW_RATE;

  let bestResult = { error: 0, flowRate: searchStartFlowRate, bhp: 0 };

  const stages = [
    { maxIterations: 100 }, // Coarse search
    { maxIterations: 200 }, // Fine search
    { maxIterations: 300 } // Ultra-fine search for precision
  ];

  for (const [stageIndex, stage] of stages.entries()) {
    let minError = Infinity;
    let lastError = Infinity;
    let flowRate = searchStartFlowRate;
    const step = (maxFlowRate - minFlowRate) / stage.maxIterations;
    for (let i = 0; i < stage.maxIterations && flowRate <= maxFlowRate; i++) {
      const bhp: number = getSimplifiedRequiredBhp(
        flowRate,
        frequencyRatio,
        ascendingOrderPumps,
        fluidDensitySpecificGravity,
        pumpWearBHPCorrectionRatio
      ).bhp;
      const currentError = Math.abs(bhp - requiredBHP);
      if (currentError < minError) {
        minError = currentError;
        bestResult = { error: currentError, flowRate, bhp };
      }
      // For stages after the first, if error grows, we've passed the
      // minimum for this granularity. Stop this stage and prepare for
      // the next, finer stage.
      if (stageIndex > 0 && i > 0 && currentError > lastError) {
        break;
      }

      lastError = currentError;
      flowRate += step;
    }

    // Exit early if result is within acceptable tolerance
    if (bestResult.error <= 0.01) {
      break;
    }

    // Prepare for the next stage by starting the search just before
    // the best-found flowrate and from the current stage.
    searchStartFlowRate = bestResult.flowRate - step;
    if (searchStartFlowRate < MIN_STARTING_FLOW_RATE)
      searchStartFlowRate = MIN_STARTING_FLOW_RATE;
    minFlowRate = searchStartFlowRate;
    maxFlowRate = minFlowRate + step * stage.maxIterations;
    if (maxFlowRate > MAX_FLOW_RATE) maxFlowRate = MAX_FLOW_RATE;
  }

  return bestResult;
};
